import { useCallback, useId, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import type { IPickerOption } from './IPickerOption';
import type { ITagInputProps } from './ITagInputProps';
import { nextEnabledIndex } from './nextEnabledIndex';
import { normalizeTagName } from './normalizeTagName';
import { tagSuggestions } from './tagSuggestions';
import { useFloating } from './useFloating';

const NEW = '\u0000new';

/**
 * Стан поля тегів (прототип onTagIn/onTagKey): фокус відкриває підказки; ↑ ↓ — виділення; Enter / Tab — виділений тег або
 * «Створити»; кома чи крапка з комою — додати введене; Backspace у порожньому полі — вилучити останній чип; Esc закриває.
 */
export function useTagInput({ chips, tags, onAdd, onRemove }: ITagInputProps) {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [hi, setHi] = useState(0);
  const input = useRef<HTMLInputElement>(null);
  const panel = useRef<HTMLDivElement>(null);
  const listId = useId();
  const sug = useMemo(() => tagSuggestions(tags, chips.map((c) => c.key), query), [tags, chips, query]);
  const options: IPickerOption[] = useMemo(() => [
    ...sug.items.map((s) => ({ value: s.key, label: s.label, dot: s.color ?? '' })),
    ...(sug.create ? [{ value: NEW, label: t('fieldControls.createTag', { name: sug.create }), icon: 'plus', accent: true }] : []),
  ], [sug, t]);
  const close = useCallback(() => { setOpen(false); setHi(0); }, []);
  const pos = useFloating(open && options.length > 0, input, panel, close, { estimate: Math.min(260, options.length * 32 + 44), minWidth: 220, matchWidth: false });
  const commit = (o: IPickerOption | undefined) => {
    if (!o) return;
    onAdd(o.value === NEW ? (sug.create ?? '') : (tags.find((x) => x.key === o.value)?.label ?? o.label));
    setQuery(''); setHi(0); setOpen(true);
  };
  const change = (v: string) => {
    if (/[,;]$/.test(v)) { const name = normalizeTagName(v); if (name) onAdd(name); setQuery(''); setHi(0); return; }
    setQuery(v); setHi(0); setOpen(true);
  };
  const onKeyDown = (e: KeyboardEvent) => {
    const k = e.key;
    const disabled = options.map(() => false);
    if (k === 'ArrowDown') { e.preventDefault(); setOpen(true); setHi((h) => nextEnabledIndex(disabled, h, 1)); }
    else if (k === 'ArrowUp') { e.preventDefault(); setHi((h) => nextEnabledIndex(disabled, h, -1)); }
    else if (k === 'Enter' || k === 'Tab') {
      if (query.trim() || (k === 'Enter' && open && options.length)) { e.preventDefault(); commit(options[hi] ?? options[0]); }
    } else if (k === 'Backspace' && !query && chips.length) { e.preventDefault(); onRemove(chips[chips.length - 1]!.key); }
  };
  const onBlur = () => setTimeout(() => {
    const a = document.activeElement;
    if (a && (input.current === a || panel.current?.contains(a))) return;
    close();
  }, 150);
  return { query, open: open && options.length > 0, hi, setHi, input, panel, listId, pos, options, commit, change, onKeyDown, onBlur, show: () => setOpen(true) };
}
